/**
 * Привлечение внимания при тревоге: звук, вибрация, мигание.
 *
 * ПРАВИЛА, ЗА КОТОРЫЕ ЗДЕСЬ ДЕРЖИМСЯ:
 *
 *  * Человек ВСЕГДА может выключить и звук, и мигание — паника не помогает
 *    эвакуации, а у кого-то светобоязнь или ребёнок на руках. Выбор помнится
 *    (localStorage) и переживает перезагрузку.
 *  * Учения не звучат: сигнал тревоги обязан означать тревогу, иначе на него
 *    перестают реагировать.
 *  * Браузер вправе запретить звук до первого касания (autoplay policy). Мы
 *    не притворяемся, что звук идёт: если заблокирован — показываем кнопку
 *    «Включить звук», и первый тап её решает. Врать про звук в ЧС нельзя.
 *
 * Звук — WebAudio-сирена (двухтоновый осциллятор), никаких файлов и сети:
 * тревога обязана звучать и офлайн.
 */

const MUTE_KEY = 'alarm-mute'
const STILL_KEY = 'alarm-still'

export function alarmMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
}

export function setAlarmMuted(mute: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, mute ? '1' : '0')
  } catch {
    // приватный режим — просто не запомнится
  }
  if (mute) stopSiren()
}

export function blinkDisabled(): boolean {
  try {
    return localStorage.getItem(STILL_KEY) === '1'
  } catch {
    return false
  }
}

export function setBlinkDisabled(still: boolean): void {
  try {
    localStorage.setItem(STILL_KEY, still ? '1' : '0')
  } catch {
    // приватный режим
  }
}

let ctx: AudioContext | null = null
let sirenTimer: ReturnType<typeof setInterval> | null = null

/** Один цикл «ви-у»: два тона по 400 мс. Негромко (gain 0.12) — сигнал должен
 *  быть слышен, а не оглушать в замкнутом помещении. */
function chirp(at: AudioContext): void {
  const osc = at.createOscillator()
  const gain = at.createGain()
  osc.type = 'square'
  osc.frequency.setValueAtTime(880, at.currentTime)
  osc.frequency.setValueAtTime(660, at.currentTime + 0.4)
  gain.gain.setValueAtTime(0.12, at.currentTime)
  gain.gain.setValueAtTime(0.0001, at.currentTime + 0.8)
  osc.connect(gain).connect(at.destination)
  osc.start()
  osc.stop(at.currentTime + 0.85)
}

/**
 * Запустить сирену. Возвращает false, если браузер заблокировал звук до
 * жеста, — вызывающий покажет кнопку «Включить звук».
 */
export function startSiren(): boolean {
  if (alarmMuted()) return true
  try {
    ctx = ctx ?? new AudioContext()
    if (ctx.state === 'suspended') {
      void ctx.resume()
      if (ctx.state === 'suspended') return false
    }
    if (sirenTimer === null) {
      chirp(ctx)
      sirenTimer = setInterval(() => {
        if (ctx && ctx.state === 'running' && !alarmMuted()) chirp(ctx)
      }, 2000)
    }
    // Вибрация телефона — короткий паттерн один раз при активации.
    navigator.vibrate?.([300, 120, 300, 120, 600])
    return ctx.state === 'running'
  } catch {
    return false
  }
}

export function stopSiren(): void {
  if (sirenTimer !== null) {
    clearInterval(sirenTimer)
    sirenTimer = null
  }
}

/** Повторить попытку после первого жеста: autoplay снимается касанием. */
export function retrySirenOnFirstGesture(onStarted: () => void): void {
  const once = (): void => {
    if (startSiren()) {
      onStarted()
      window.removeEventListener('pointerdown', once)
    }
  }
  window.addEventListener('pointerdown', once)
}
